import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  CardActions,
  Box,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import CTAButton from "./CTAButton";

const ServiceCard = ({ title, description, image, link, icon }) => {
  const theme = useTheme();

  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.95 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{
        duration: 0.6,
        type: "spring",
        stiffness: 100,
      }}
      whileHover={{
        y: -12,
        transition: { duration: 0.3, ease: "easeOut" },
      }}
    >
      <Card
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
          maxWidth: 545,
          borderRadius: 4,
          background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
          border: `1px solid ${theme.palette.divider}`,
          overflow: "hidden",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            zIndex: 2,
          },
          "&:hover": {
            transform: "translateY(-12px)",
            boxShadow: `
              0 25px 50px -12px rgba(0, 0, 0, 0.25),
              0 0 30px -10px ${theme.palette.primary.main}40
            `,
            "& .card-image-overlay": {
              opacity: 0.8,
            },
            "& .service-icon": {
              transform: "scale(1.1) rotate(5deg)",
            },
          },
        }}
      >
        {/* Image Container with Overlay */}
        <Box sx={{ position: "relative", overflow: "hidden" }}>
          <CardMedia
            component="img"
            height="240"
            image={image}
            alt={title}
            sx={{
              objectFit: "cover",
              maxHeight: 240,
              width: "100%",
              transition: "transform 0.4s ease",
              "&:hover": {
                transform: "scale(1.05)",
              },
            }}
          />

          {/* Gradient Overlay */}
          <Box
            className="card-image-overlay"
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "60%",
              background: `linear-gradient(to top, 
                ${theme.palette.common.black} 0%, 
                transparent 100%)`,
              opacity: 0.6,
              transition: "opacity 0.3s ease",
            }}
          />

          {/* Service Icon */}
          {icon && (
            <motion.div
              className="service-icon"
              initial={{ scale: 0.8, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              style={{
                position: "absolute",
                top: 16,
                right: 16,
                background: "rgba(255, 255, 255, 0.95)",
                borderRadius: "50%",
                width: 60,
                height: 60,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                backdropFilter: "blur(10px)",
                border: `2px solid ${theme.palette.primary.main}20`,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontSize: "1.5rem",
                  lineHeight: 1,
                }}
              >
                {icon}
              </Typography>
            </motion.div>
          )}

          {/* Title on Image */}
          <Typography
            variant="h5"
            component="h3"
            sx={{
              position: "absolute",
              bottom: 16,
              left: 16,
              right: 16,
              color: "white",
              fontWeight: 700,
              fontSize: { xs: "1.25rem", md: "1.5rem" },
              textShadow: "0 2px 8px rgba(0,0,0,0.6)",
              lineHeight: 1.2,
              background:
                "linear-gradient(90deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
            }}
          >
            {title}
          </Typography>
        </Box>

        <CardContent
          sx={{
            flexGrow: 1,
            px: 3,
            pt: 3,
            pb: 1,
            background: `linear-gradient(to bottom, 
              ${theme.palette.background.paper} 0%, 
              ${theme.palette.background.default} 100%)`,
          }}
        >
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              lineHeight: 1.7,
              fontSize: "0.95rem",
              fontWeight: 400,
              color: theme.palette.text.secondary,
            }}
          >
            {description}
          </Typography>
        </CardContent>

        <CardActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <CTAButton
            to={link}
            variant="contained"
            fullWidth
            size="large"
            sx={{
              background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              color: "white",
              fontWeight: 600,
              fontSize: "0.95rem",
              padding: "12px 24px",
              borderRadius: 3,
              textTransform: "none",
              boxShadow: `0 4px 15px ${theme.palette.primary.main}40`,
              transition: "all 0.3s ease",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: "-100%",
                width: "100%",
                height: "100%",
                background:
                  "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                transition: "left 0.5s ease",
              },
              "&:hover": {
                boxShadow: `0 8px 25px ${theme.palette.primary.main}60`,
                transform: "translateY(-2px)",
                "&::before": {
                  left: "100%",
                },
              },
            }}
          >
            Explore Service
          </CTAButton>
        </CardActions>

        {/* Hover Effect Border */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            border: `2px solid transparent`,
            borderRadius: 4,
            background: `linear-gradient(145deg, ${theme.palette.primary.main}20, ${theme.palette.secondary.main}20) border-box`,
            mask: `linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)`,
            maskComposite: "exclude",
            opacity: 0,
            transition: "opacity 0.3s ease",
            pointerEvents: "none",
            "&:hover": {
              opacity: 1,
            },
          }}
        />
      </Card>
    </motion.div>
  );
};

export default ServiceCard;
